import styles from "@styles/Main.module.css";
import { CSSProperties, useCallback, useContext, useEffect, useState } from "react";
import { DeviceProps } from "@components/Root";
import { useViewport } from "@components/ViewportManager";
import { Slider } from "@components/Slider";
import { EScenario } from "@utils/Scenarios";
import { AppData } from "@App";
import { Details, Home, Metrics, Temperature } from "@components/Icons";
import { Color } from "three";
import { LoadingContext } from "@components/Loader";
import { mapTime, maxTime, minTime } from "@utils/TimeMap";

const background = new Color();
const startInner = new Color("#ebebeb");
const startOuter = new Color("#969696");
const endInner = new Color("#eea849");
const endOuter = new Color("#f46b45");

export function Main({ mobile, scale }: DeviceProps) {
    const { loaded } = useContext(LoadingContext);
    const [scenario, _setScenario] = useState(EScenario.Default);
    const [prevScenario, _setPrevScenario] = useState(EScenario.Default);
    const [passedTime, setPassedTime] = useState(0);
    const setScenario = useCallback((scenario: React.SetStateAction<EScenario>) => {
        _setScenario((prev) => {
            _setPrevScenario(prev);
            if (typeof scenario === "function") return scenario(prev);
            return scenario;
        });
    }, [_setScenario, _setPrevScenario]);

    const view = useViewport<HTMLDivElement, AppData>("main", { scenario, prevScenario, passedTime });

    const rootStyle: CSSProperties = {};
    background.lerpColors(startInner, endInner, mapTime(scenario === EScenario.Temperature ? passedTime : 0, 0, 1));
    (rootStyle as any)["--innerColor"] = `#${background.getHexString()}`;
    background.lerpColors(startOuter, endOuter, mapTime(scenario === EScenario.Temperature ? passedTime : 0, 0, 1));
    (rootStyle as any)["--outerColor"] = `#${background.getHexString()}`;

    function makeUi(type: "button" | "line", scene: EScenario) {
        const classes = [styles[type]];
        if (scenario > scene) classes.push(styles.activated);
        if (scenario >= scene) classes.push(styles.active);
        const out: any = {
            className: classes.join(" "),
        };
        if (type === "button") out.onClick = () => setScenario(scene);
        return out;
    }

    useEffect(() => {
        if (loaded) setScenario(s => {
            if (!loaded || s !== EScenario.Default) return s;
            return EScenario.Home;
        });
    }, [loaded, setScenario]);

    return (
        <>
            <div ref={view} className={styles.root} style={rootStyle} />
            <div className={styles.ui}>
                <div className={styles.content}>
                    <div className="row">
                        <div {...makeUi("button", EScenario.Home)} data-name="Home"><Home color={"inherit"} /></div>
                        <div {...makeUi("line", EScenario.Explode)} />
                        <div {...makeUi("button", EScenario.Explode)} data-name="Details"><Details color={"inherit"} /></div>
                        <div {...makeUi("line", EScenario.Temperature)} />
                        <div {...makeUi("button", EScenario.Temperature)} data-name="Temperature"><Temperature color={"inherit"} /></div>
                        {/* <div {...makeUi("line", EScenario.Metrics)} />
                        <div {...makeUi("button", EScenario.Metrics)} data-name="Sustainability Metrics"><Metrics color={"inherit"} /></div> */}
                    </div>
                    <div className={`row ${styles.sliderRow} ${scenario === EScenario.Temperature ? styles.on : styles.off}`}>
                        <div className={`col ${styles.slider}`}>
                            <span>Drag the slider to compare the thermal performance of TailorTemp® to an EPS solution including graphite.</span>
                            <Slider disabled={scenario !== EScenario.Temperature} initial={passedTime} label={`${passedTime.toFixed(0)} hour${passedTime.toFixed(0) === "1" ? "" : "s"}`} size={40} thumbsize={20} thickness={4} from={minTime} to={maxTime} onDrag={setPassedTime} />
                        </div>
                    </div>
                </div>
                <div className={`${styles.content} ${styles.explode}`}>
                    <div className={`row ${scenario === EScenario.Home ? styles.on : styles.off}`}>
                        <div className={`${styles.cta}`} onClick={() => setScenario(EScenario.Home2)}>Click here to see how TailorTemp® works</div>
                    </div>
                </div>
                <div className={`${styles.content} ${styles.info} ${scenario === EScenario.Home ? styles.on : styles.off}`}>
                    <div className={`${styles.center}`}>
                        <h2 className="accent glow">TailorTemp®</h2>
                        <h4>A range of fibre-based packaging solutions to replace EPS for cold chain logistics</h4>
                    </div>
                </div>
                <div className={`${styles.content} ${styles.info} ${scenario === EScenario.Home2 ? styles.on : styles.off}`}>
                    <div className={`${styles.left}`}>
                        <h2 className="accent glow">TailorTemp®</h2>
                        <h4 style={{ marginBottom: "16rem" }}>Our first 100% fibre-based packaging alternative to EPS, offering temperature control for up to 72 hours for pharmaceutical products requiring 2-8°C*</h4>
                    </div>
                    <div className={`${styles.right}`}>
                        <h2>How does it work?</h2>
                        <ol>
                            <li>Define your need:</li>
                            <ul>
                                <li>Your products' masses and dimensions</li>
                                <li>Initial & expected final products' temperatures</li>
                                <li>Transport duration</li>
                                <li>External temperature profile</li>
                                <li>Your coolant's specifications</li>
                            </ul>
                            <li>Run the DS&nbsp;Smith parametric model to:</li>
                            <ul>
                                <li>Define the right thermal liner</li>
                                <li>Define the coolant amount</li>
                                <li>Identify the tailored packaging's dimensions</li>
                            </ul>
                            <li>DS&nbsp;Smith to supply a custom packaging solution that will be:</li>
                            <ul>
                                <li>100% plastic-free, curbside recyclable packaging</li>
                                <li>Optimised for inbound and outbound logistics</li>
                                <li>A 40% reduction in CO<sub>2</sub> footprint <i>(based on internal benchmark (The Circular Design Metrics), LCA critical review pending)</i></li>
                            </ul>
                        </ol>
                    </div>
                </div>
                <div className={`${styles.content} ${styles.infoDisclaimer}`}>
                    <div className="row">
                        <span className={scenario === EScenario.Home2 ? styles.on : styles.off}>
                            * Based on DSS internal lab testing data. Tested against ISTA 7D Summer profile.
                        </span>
                    </div>
                </div>
                <div className={`${styles.content} ${styles.tempDisclaimer}`}>
                    <div className="row">
                        <span className={scenario === EScenario.Temperature ? styles.on : styles.off}>
                            * Based on DSS internal lab testing data. Tested against ISTA 7D Summer 48-hour profile.
                        </span>
                    </div>
                </div>
                <div className={`${styles.content} ${styles.tempDisclaimerRight}`}>
                    <div className="row">
                        <span className={scenario === EScenario.Temperature ? styles.on : styles.off}>
                            This example is based on 48-hour data. If you would like to see how our solution performs for 72-hours please <a href="https://www.dssmith.com/products-services/packaging-solutions/industrial-packaging/tailortemp" target="_blank" rel="noopener noreferrer">get in touch</a>.
                        </span>
                    </div>
                </div>
                <div className={`${styles.content} ${styles.metricsDisclaimer}`}>
                    <div className="row">
                        <span className={scenario === EScenario.Metrics ? styles.on : styles.off}>
                            * Based on a case study but each solution will deliver different benefits
                        </span>
                    </div>
                </div>
            </div>
        </>
    );
}